import React, { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

const TOUR_STEPS = [
  {
    title: 'Welcome to FarmDash! 🚀',
    description: 'Your professional dashboard for tracking airdrops. I\'ll show you how to get started in 3 simple steps.',
  },
  {
    title: '1. Add your Airdrops 📋',
    description: 'First, go to the "Airdrops" tab and add the projects you\'re farming. You can configure type, capital, priority and more.',
  },
  {
    title: '2. Define your Targets 🎯',
    description: 'In "Targets" set your weekly and final objectives. You can track points, volume, or both. The dashboard will calculate your progress automatically!',
  },
  {
    title: '3. Register your Activity 📊',
    description: 'Each week, go to "Add" to register your volume, costs and points. This way you can see your progress and ROI in real time.',
  },
  {
    title: 'Ready to Start! 🎉',
    description: 'You now know the basics. Explore ROI to see your projections, History to review your activity, and customize everything to your liking. Good luck farming!',
  }
];

export const OnboardingTour = ({ onComplete, userId }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkTourStatus = async () => {
      // If no userId, don't show the tour (user not logged in)
      if (!userId) {
        setIsLoading(false);
        return;
      }

      try {
        const userDocRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          // Show tour only if onboardingCompleted is not true
          if (!data.onboardingCompleted) {
            setIsVisible(true);
          }
        } else {
          // New user (no document yet), show the tour
          setIsVisible(true);
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        // On error, don't show the tour to avoid blocking users
      }
      
      setIsLoading(false);
    };

    checkTourStatus();
  }, [userId]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeTour = async () => {
    // Save to Firestore
    if (userId) {
      try {
        const userDocRef = doc(db, 'users', userId);
        await setDoc(userDocRef, {
          onboardingCompleted: true,
          onboardingCompletedAt: new Date().toISOString()
        }, { merge: true });
      } catch (error) {
        console.error('Error saving onboarding status:', error);
      }
    }
    
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const skipTour = () => {
    completeTour();
  };

  // Don't render while loading or if not visible
  if (isLoading || !isVisible) return null;

  const step = TOUR_STEPS[currentStep];
  const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;

  return (
    <div className="fixed inset-0 z-[99999]">
      <div className="fixed inset-0 bg-black/75 z-[99999]" />
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[100000] max-w-md w-[90%] bg-slate-800 border-2 border-blue-500 rounded-xl p-6 shadow-2xl">
        <div className="absolute top-0 left-0 right-0 h-1 bg-slate-700 rounded-t-xl overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        <button
          onClick={skipTour}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
        >
          <X size={20} />
        </button>

        <div className="mt-4">
          <h3 className="text-2xl font-bold text-white mb-3">{step.title}</h3>
          <p className="text-slate-300 mb-6 leading-relaxed">{step.description}</p>

          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-slate-400">
              Step {currentStep + 1} of {TOUR_STEPS.length}
            </div>
            <div className="flex gap-1">
              {TOUR_STEPS.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-2 rounded-full transition-colors ${
                    index === currentStep
                      ? 'bg-blue-500'
                      : index < currentStep
                      ? 'bg-green-500'
                      : 'bg-slate-600'
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            {currentStep > 0 && (
              <button
                onClick={handlePrevious}
                className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition-colors"
              >
                <ArrowLeft size={18} />
                Previous
              </button>
            )}
            <button
              onClick={handleNext}
              className="flex-1 bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold transition-all"
            >
              {currentStep === TOUR_STEPS.length - 1 ? "Let's Start!" : 'Next'}
              {currentStep < TOUR_STEPS.length - 1 && <ArrowRight size={18} />}
            </button>
          </div>

          {currentStep < TOUR_STEPS.length - 1 && (
            <button
              onClick={skipTour}
              className="w-full text-center text-sm text-slate-400 hover:text-slate-300 mt-3 py-2 transition-colors"
            >
              Skip tour
            </button>
          )}
        </div>
      </div>
    </div>
  );
};